import { Component, inject, isDevMode } from '@angular/core';
import { ConfigInstanceToken } from '../../interfaces/config.interface';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { JsonTimesheetModel, TimesheetModel } from '../../models/low-level/timesheet.model';
import { FileDexieService } from '../../services/file-storage/file-dexie.service';
import { catchError, finalize, map, mergeMap, take } from 'rxjs/operators';
import { TIMESHEET_KEYS } from '../../enums/timesheet.enum';
import { decompress, extractDigitsAsNumber } from './pace-spector.utils';
import { TimesheetSpectorComponent } from './timesheet-spector/timesheet-spector.component';
import { AddressSpectorComponent } from './address-spector/address-spector.component';
import moment from 'moment/moment';
import { getUuidV4 } from '../../utilities/generic-utilities';
import { LogInstanceToken, PaceInsightsService } from '@evorait/pace-logging';
import { safeStringify } from '@evorait/pace-components';
import { of } from 'rxjs';
import { ADDRESSES_KEY } from '../../services/timesheet/storage-address.service';
import { ConcurrentUsageService } from '../../services/concurrent-usage/concurrent-usage.service';
import { AlertComponent } from 'ngx-bootstrap/alert';
import { TabDirective, TabsetComponent } from 'ngx-bootstrap/tabs';
import { StorageTimesheetService } from '../../services/timesheet/storage-timesheet.service';
import { TimesheetAndExpensesInstanceToken } from '../../services/timesheet/timesheet-and-expenses.interface';
import { AddressState } from '../../state/address';

@Component({
    selector: 'pace-core-pace-spector',
    imports: [
        CommonModule,
        TranslateModule,
        FormsModule,
        TimesheetSpectorComponent,
        AddressSpectorComponent,
        AlertComponent,
        TabsetComponent,
        TabDirective,
    ],
    templateUrl: './pace-spector.component.html',
    styleUrl: './pace-spector.component.css',
})
export class PaceSpectorComponent {
    private readonly timesheetAndExpensesService = inject(TimesheetAndExpensesInstanceToken);
    private readonly timesheetStorage = inject(FileDexieService);
    private readonly concurrentStorage = inject(ConcurrentUsageService);
    private readonly timesheetConcurrentStorageService = inject(StorageTimesheetService);
    private readonly log = inject(LogInstanceToken);
    private readonly configService = inject(ConfigInstanceToken);
    private readonly paceInsightsService = inject(PaceInsightsService);

    public TIMESHEET_KEYS = TIMESHEET_KEYS;

    public unlocked = false;
    public session = this.paceInsightsService.getClientSession();
    public gateLabelId = 'pace-spector-gate-label';
    public gateInputId = 'pace-spector-gate-input';
    public loading: { [k: string]: string } = {};
    public errors: string[] = [];

    public accessCode: string | null = null;

    public addresses$ = this.timesheetStorage.selectFile(ADDRESSES_KEY).pipe(
        mergeMap(async (addresses) => {
            return decompress<AddressState[]>(addresses);
        }),
    );
    public submittedTimesheet$ = this.concurrentStorage.liveQueryDataForKey$(TIMESHEET_KEYS.SUBMITTED_TIMESHEET).pipe(
        map((tsData) => ({
            key: tsData?.key,
            timestamp: tsData?.timestamp,
            value: JSON.parse(tsData?.value ?? '') as JsonTimesheetModel,
        })),
    );
    public currentTimesheet$ = this.concurrentStorage.liveQueryDataForKey$(TIMESHEET_KEYS.CURRENT_TIMESHEET).pipe(
        map((tsData) => ({
            key: tsData?.key,
            timestamp: tsData?.timestamp,
            value: JSON.parse(tsData?.value ?? '') as JsonTimesheetModel,
        })),
    );

    public unlock(): void {
        const parsed = this.accessCode?.trim();
        if (!parsed?.length) {
            return;
        }
        let accessCode = 67;

        const today = new Date();
        const dayPart = today.getDate();
        const monthPart = today.getMonth() + 1;
        accessCode += (dayPart + monthPart) * 2;

        const frontId = this.configService.getUserFrontId();
        if (frontId?.length > 3) {
            const fronNum = extractDigitsAsNumber(frontId);
            if (fronNum) {
                accessCode += fronNum;
            }
        }

        if (parsed === accessCode.toString() || (isDevMode() && parsed === '-3010')) {
            this.unlocked = true;
            this.paceInsightsService.trackEvent('PaceSpector::unlock', { status: 'success' });
        }
    }

    public resetTimesheet(timesheetKey: TIMESHEET_KEYS): void {
        this.paceInsightsService.trackEvent('PaceSpector::action', { action: 'resetTimesheet', timesheetKey });
        this.log.info(`resetTimesheet ${timesheetKey}`);
        const uuid = getUuidV4();
        this.loading[uuid] = `resetting ${timesheetKey}`;
        const emptyTimesheet = new TimesheetModel(moment.invalid(), [], [], [], false);
        emptyTimesheet.saveTimestamp = Date.now();
        this.timesheetConcurrentStorageService
            .saveTimesheet$(timesheetKey, emptyTimesheet)
            .pipe(
                take(1),
                catchError((e) => {
                    this.log.error('resetTimesheet error', e);
                    this.errors.push(`resetTimesheet error: ${safeStringify(e)}`);
                    return of(undefined);
                }),
                finalize(() => {
                    delete this.loading[uuid];
                }),
            )
            .subscribe(() => {});
    }

    public deleteAddresses(): void {
        this.paceInsightsService.trackEvent('PaceSpector::action', { action: 'deleteAddresses' });
        this.log.info('deleteAddresses');
        const uuid = getUuidV4();
        this.loading[uuid] = `resetting addresses`;
        this.timesheetAndExpensesService
            .saveAddresses([])
            .pipe(
                take(1),
                catchError((e) => {
                    this.log.error('deleteAddresses error', e);
                    this.errors.push(`deleteAddresses error: ${safeStringify(e)}`);
                    return of(undefined);
                }),
                finalize(() => {
                    delete this.loading[uuid];
                }),
            )
            .subscribe(() => {});
    }

    public downloadJson(data: unknown): void {
        const json = JSON.stringify(
            {
                frontId: this.configService.getUserFrontId(),
                session: this.paceInsightsService.getClientSession(),
                data,
            },
            null,
            2,
        );
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = `pace-inspector-${new Date().toISOString()}.json`;

        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        URL.revokeObjectURL(url);
    }
}
